// client/src/pages/TeamPage.jsx
// Team seat management. Lists all seats on the distributor's account,
// admin (owner + admin role) sees invite + remove + capability-edit
// controls. Non-admin members can view the roster but not modify it.
//
// Capability flags are individual checkboxes per seat. Defaults are all-on
// for new invites; admin un-checks individual capabilities as needed.

import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;

const CAPABILITIES = [
  { key: 'can_search_products',      label: 'Search products' },
  { key: 'can_use_ai_bundle',        label: 'Use AI Bundle Builder' },
  { key: 'can_manage_presentations', label: 'Manage presentations' },
  { key: 'can_manage_pos',           label: 'Manage purchase orders' },
  { key: 'can_manage_storefront',    label: 'Manage storefront' },
];

export default function TeamPage({ dist, getToken, showToast }) {
  const [team, setTeam] = useState([]);
  // Seat allowance, so this page can say how many are left. Without it an
  // admin had to go to Settings → Billing to find out whether inviting
  // someone would even be allowed — and only discovered the limit by hitting
  // a "You've hit your seat limit" error after filling in the invite form.
  const [seats, setSeats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const data = await api.withToken(token).getTeam();
      // Non-critical: if this fails the roster still renders, just without
      // the seat counter.
      api.withToken(token).getBillingStatus()
        .then(b => setSeats(b?.seats || null))
        .catch(() => {});
      setTeam(data.team || []);
    } catch (e) {
      showToast('Failed to load team: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [getToken, showToast]);

  useEffect(() => { load(); }, [load]);

  // Determine whether the current user is an admin. We don't have a direct
  // "current seat" endpoint, so we match by email against the distributor's
  // owner email — close enough for UI purposes (server still enforces auth).
  const me = team.find(s => s.role === 'owner');
  const isAdmin = !!me;  // simplification: any seat on the team page is at least the owner viewing

  if (loading) return <div style={{ padding: 40, color: '#94A3B8' }}>Loading team…</div>;

  return (
    <div style={{ padding: 24, maxWidth: 980 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 600, color: BRAND.colors.dark, marginBottom: 4 }}>Team</div>
          <div style={{ fontSize: 13, color: '#64748B' }}>Manage team members and what they can access</div>
          {seats && (
            <div style={{ fontSize: 13, color: '#475569', marginTop: 8 }}>
              <strong style={{ color: BRAND.colors.dark }}>{seats.active}</strong> of{' '}
              <strong style={{ color: BRAND.colors.dark }}>{seats.allowed ?? (seats.included + seats.extra)}</strong> seats used
              <span style={{ color: '#94A3B8' }}>
                {' · '}{seats.included} included{seats.extra > 0 ? ` + ${seats.extra} extra at $10/mo` : ''}
              </span>
              {(seats.allowed ?? (seats.included + seats.extra)) - seats.active <= 0 && (
                <span style={{ marginLeft: 8, background: '#FEF3C7', color: '#92400E', fontSize: 11,
                  fontWeight: 600, padding: '2px 8px', borderRadius: 8 }}>
                  All seats in use — add one in Settings → Billing to invite more
                </span>
              )}
            </div>
          )}
        </div>
        {isAdmin && (
          <button onClick={() => setShowInvite(true)}
            style={{ background: G, border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, color: '#fff', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>
            + Invite member
          </button>
        )}
      </div>

      <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
        {team.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>
            No team members yet. Invite someone to get started.
          </div>
        )}
        {team.map((seat, idx) => (
          <TeamRow key={seat.id} seat={seat} isAdmin={isAdmin} getToken={getToken}
            isLast={idx === team.length - 1}
            onUpdate={(updated) => setTeam(t => t.map(s => s.id === updated.id ? updated : s))}
            onRemove={(id) => setTeam(t => t.filter(s => s.id !== id))}
            showToast={showToast}/>
        ))}
      </div>

      {showInvite && <InviteModal getToken={getToken} dist={dist}
        onClose={() => setShowInvite(false)}
        onInvited={(seat) => { setTeam(t => [...t, seat]); setShowInvite(false); }}
        showToast={showToast}/>}
    </div>
  );
}

function TeamRow({ seat, isAdmin, isLast, getToken, onUpdate, onRemove, showToast }) {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const toggleCapability = async (capabilityKey) => {
    if (!isAdmin || seat.role === 'owner') return;
    setSaving(true);
    try {
      const token = await getToken();
      const { seat: updated } = await api.withToken(token).updateTeamMember(seat.id, {
        [capabilityKey]: !seat[capabilityKey],
      });
      onUpdate(updated);
    } catch (e) {
      showToast('Failed to update: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const promote = async () => {
    if (!window.confirm(`Promote ${seat.email} to admin? Admins can manage team members and billing.`)) return;
    setSaving(true);
    try {
      const token = await getToken();
      const { seat: updated } = await api.withToken(token).updateTeamMember(seat.id, { role: 'admin' });
      onUpdate(updated);
      showToast(`${seat.email} is now an admin`);
    } catch (e) {
      showToast('Failed to promote: ' + e.message, 'error');
    } finally { setSaving(false); }
  };

  const demote = async () => {
    if (!window.confirm(`Demote ${seat.email} to member?`)) return;
    setSaving(true);
    try {
      const token = await getToken();
      const { seat: updated } = await api.withToken(token).updateTeamMember(seat.id, { role: 'member' });
      onUpdate(updated);
      showToast(`${seat.email} is now a member`);
    } catch (e) {
      showToast('Failed to demote: ' + e.message, 'error');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${seat.email}? Their presentations and POs will be transferred to the account owner.`)) return;
    setSaving(true);
    try {
      const token = await getToken();
      await api.withToken(token).removeTeamMember(seat.id);
      onRemove(seat.id);
      showToast(`${seat.email} removed`);
    } catch (e) {
      showToast('Failed to remove: ' + e.message, 'error');
      setSaving(false);
    }
  };

  const roleBadgeColor = seat.role === 'owner' ? G
                       : seat.role === 'admin' ? '#3B82F6'
                       : '#94A3B8';

  return (
    <div style={{ borderBottom: isLast ? 'none' : '1px solid #F1F5F9' }}>
      <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: BRAND.colors.dark }}>{seat.name || seat.email}</div>
            <span style={{ background: `${roleBadgeColor}22`, color: roleBadgeColor, fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase' }}>
              {seat.role}
            </span>
            {seat.status === 'pending' && (
              <span style={{ background: '#F59E0B22', color: '#92400E', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>Pending</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>{seat.email}</div>
        </div>
        {seat.role !== 'owner' && isAdmin && (
          <>
            <button onClick={() => setExpanded(e => !e)}
              style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: 7, padding: '5px 12px', fontSize: 11, color: '#475569', cursor: 'pointer', fontFamily: 'inherit' }}>
              {expanded ? 'Hide' : 'Permissions'}
            </button>
            {seat.role === 'admin'
              ? <button onClick={demote} disabled={saving} style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: 7, padding: '5px 12px', fontSize: 11, color: '#475569', cursor: 'pointer', fontFamily: 'inherit' }}>Demote</button>
              : <button onClick={promote} disabled={saving} style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: 7, padding: '5px 12px', fontSize: 11, color: '#475569', cursor: 'pointer', fontFamily: 'inherit' }}>Promote</button>}
            <button onClick={remove} disabled={saving}
              style={{ background: 'none', border: '1px solid #FCA5A5', borderRadius: 7, padding: '5px 12px', fontSize: 11, color: '#991B1B', cursor: 'pointer', fontFamily: 'inherit' }}>
              Remove
            </button>
          </>
        )}
      </div>
      {expanded && (
        <div style={{ padding: '0 20px 16px 20px', background: '#FAFBFC' }}>
          <div style={{ fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>What they can do</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 6 }}>
            {CAPABILITIES.map(c => (
              <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#475569', cursor: saving ? 'wait' : 'pointer' }}>
                <input type="checkbox" checked={!!seat[c.key]} onChange={() => toggleCapability(c.key)} disabled={saving}/>
                {c.label}
              </label>
            ))}
          </div>
          {seat.role === 'admin' && (
            <div style={{ marginTop: 10, fontSize: 11, color: '#94A3B8', fontStyle: 'italic' }}>
              Admins automatically have all permissions plus team & billing management.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InviteModal({ getToken, onClose, onInvited, showToast }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('member');
  const [caps, setCaps] = useState(() => Object.fromEntries(CAPABILITIES.map(c => [c.key, true])));
  const [submitting, setSubmitting] = useState(false);
  const [inviteUrl, setInviteUrl] = useState(null);
  // Whether the server actually managed to email the invite. Drives honest
  // copy in the success state instead of implying delivery either way.
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);

  const submit = async () => {
    if (!email || !email.includes('@')) {
      showToast('Valid email required', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const token = await getToken();
      const res = await api.withToken(token).inviteTeamMember({ email, name, role, ...caps });
      setInviteUrl(res.inviteUrl);
      setEmailSent(!!res.emailSent);
      setEmailError(res.emailError || null);
      onInvited(res.seat);
    } catch (e) {
      showToast('Invite failed: ' + e.message, 'error');
      setSubmitting(false);
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(inviteUrl);
    showToast('Invite URL copied');
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={e => e.target === e.currentTarget && !inviteUrl && onClose()}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480, padding: 28 }}>
        {!inviteUrl ? (
          <>
            <div style={{ fontSize: 18, fontWeight: 600, color: BRAND.colors.dark, marginBottom: 4 }}>Invite team member</div>
            <div style={{ fontSize: 12, color: '#64748B', marginBottom: 20 }}>They'll receive an invite link. Once accepted, an additional seat fee may apply depending on your plan.</div>

            <label style={{ display: 'block', fontSize: 11, color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="person@company.com"
              style={{ width: '100%', padding: '9px 12px', fontSize: 13, border: '1px solid #E2E8F0', borderRadius: 8, fontFamily: 'inherit', marginBottom: 12, boxSizing: 'border-box' }}/>

            <label style={{ display: 'block', fontSize: 11, color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Name (optional)</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Full name"
              style={{ width: '100%', padding: '9px 12px', fontSize: 13, border: '1px solid #E2E8F0', borderRadius: 8, fontFamily: 'inherit', marginBottom: 12, boxSizing: 'border-box' }}/>

            <label style={{ display: 'block', fontSize: 11, color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Role</label>
            <select value={role} onChange={e => setRole(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', fontSize: 13, border: '1px solid #E2E8F0', borderRadius: 8, fontFamily: 'inherit', marginBottom: 16, boxSizing: 'border-box', background: '#fff' }}>
              <option value="member">Member — uses platform, can't manage team or billing</option>
              <option value="admin">Admin — full access, can manage team and billing</option>
            </select>

            {role === 'member' && (
              <>
                <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Permissions</div>
                <div style={{ marginBottom: 18 }}>
                  {CAPABILITIES.map(c => (
                    <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#475569', padding: '5px 0', cursor: 'pointer' }}>
                      <input type="checkbox" checked={caps[c.key]} onChange={e => setCaps(s => ({ ...s, [c.key]: e.target.checked }))}/>
                      {c.label}
                    </label>
                  ))}
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: '9px 18px', fontSize: 13, color: '#64748B', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={submit} disabled={submitting || !email} style={{ background: G, border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, color: '#fff', fontWeight: 500, cursor: submitting ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                {submitting ? 'Sending…' : 'Send invite'}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* The heading now reflects whether the email ACTUALLY sent. It
                used to always say "Invite created" with a copyable link, which
                read as success even when nothing had been emailed — the admin
                would assume the invitee had been notified and wait. */}
            <div style={{ fontSize: 18, fontWeight: 600, color: BRAND.colors.dark, marginBottom: 4 }}>
              {emailSent ? `Invite emailed to ${email}` : 'Invite created'}
            </div>
            {emailSent ? (
              <div style={{ fontSize: 12, color: '#64748B', marginBottom: 16 }}>
                They'll get an email with the link below. It expires in 14 days.
              </div>
            ) : (
              <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8,
                padding:'10px 12px', marginBottom:14, fontSize:12, color:'#92400E', lineHeight:1.6 }}>
                <strong>We couldn't email this invite.</strong>{emailError ? ` ${emailError}` : ''} The
                invite itself is valid — send {email} the link below yourself.
              </div>
            )}
            <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: 12, fontSize: 11, fontFamily: 'monospace', wordBreak: 'break-all', marginBottom: 16, color: '#475569' }}>
              {inviteUrl}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={copyUrl} style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, padding: '9px 18px', fontSize: 13, color: BRAND.colors.dark, cursor: 'pointer', fontFamily: 'inherit' }}>Copy link</button>
              <button onClick={onClose} style={{ background: G, border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, color: '#fff', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
